import axios from "axios";
import { apiBaseUrl } from "../config";
import { getCurrentSessionCode, getHostToken } from "../session/hostTokens";
import { getHostKey } from "../session/hostKey";

export const HOST_TOKEN_HEADER = "X-Host-Token";
export const SESSION_CODE_HEADER = "X-Session-Code";
export const HOST_KEY_HEADER = "X-Host-Key";

const REQUEST_TIMEOUT_MS = 15000;
const MAX_RETRIES = 3;
const RETRIED_STATUSES = [408, 502, 503, 504];

const isTransient = (error) =>
  !error.response ||
  error.code === "ECONNABORTED" ||
  error.code === "ERR_NETWORK" ||
  RETRIED_STATUSES.includes(error.response.status);

const wantsRetry = (config) => {
  if (config.retry === false) return false;
  if (config.retry === true) return true;
  return (config.method || "get").toLowerCase() === "get";
};

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Retries failed requests with exponential backoff. GETs are retried by default; other
 * requests only when sent with `{ retry: true }` (safe for the idempotent add/remove calls,
 * not for "current position" updates that must not be replayed out of order). Only network
 * errors, timeouts and 408/502/503/504 responses are retried, and never while the browser
 * reports being offline.
 */
export function attachRetry(instance, { maxRetries = MAX_RETRIES, baseDelayMs = 500 } = {}) {
  instance.interceptors.response.use(undefined, async (error) => {
    const config = error.config;
    const attempt = (config?.retryCount || 0) + 1;
    const offline = typeof navigator !== "undefined" && navigator.onLine === false;
    if (!config || axios.isCancel(error) || attempt > maxRetries || offline || !wantsRetry(config) || !isTransient(error)) {
      throw error;
    }
    config.retryCount = attempt;
    await wait(baseDelayMs * 2 ** (attempt - 1) + Math.random() * baseDelayMs * 0.5);
    return instance(config);
  });
  return instance;
}

export const isNotFound = (error) => error?.response?.status === 404;

export const isForbidden = (error) => error?.response?.status === 403;

export const isRateLimited = (error) => error?.response?.status === 429;

/**
 * What the server said about a refused request, when it said something a person can read:
 * the detail (or title) of a problem-details answer, or a plain-text 4xx body. Null for
 * network failures and server errors, whose wording is not meant for users.
 */
export const serverMessage = (error) => {
  const response = error?.response;
  if (!response || response.status >= 500) return null;
  const data = response.data;
  if (typeof data === "string") return data.trim() || null;
  if (data && typeof data === "object") {
    const text = data.detail || data.title;
    return typeof text === "string" && text.trim() ? text.trim() : null;
  }
  return null;
};

/** The notice to show for a failed change: the server's own explanation when it gave one, otherwise the fallback. */
export const failureMessage = (error, fallback) => {
  if (isRateLimited(error)) return "Too many changes in a short time. Please wait a moment and try again.";
  return serverMessage(error) || fallback;
};

const client = attachRetry(axios.create({ baseURL: apiBaseUrl, timeout: REQUEST_TIMEOUT_MS }));

// Identify the caller: the host key of this device, the current session (song and library
// endpoints have no session in their route and take it from this header; the server then
// checks the key or the token against that session), and the session's token when this
// browser holds one.
client.interceptors.request.use((config) => {
  const hostKey = getHostKey();
  if (hostKey) config.headers[HOST_KEY_HEADER] = hostKey;

  const code = getCurrentSessionCode();
  if (code) config.headers[SESSION_CODE_HEADER] = code;

  const token = getHostToken(code);
  if (token) config.headers[HOST_TOKEN_HEADER] = token;
  return config;
});

export default client;
